// Client mirror of functions-common/src/charts/datasets/work.ts. Keep in sync.

import type { DatasetDefinition } from '../types.js'

export const workDataset: DatasetDefinition = {
  key: 'work',
  labelKey: 'NLSChartDatasetWork',
  fields: [
    { name: 'type', labelKey: 'NLSChartFieldType', kind: 'dimension', type: 'string' },
    { name: 'status', labelKey: 'NLSChartFieldStatus', kind: 'dimension', type: 'string' },
    { name: 'priority', labelKey: 'NLSChartFieldPriority', kind: 'dimension', type: 'string' },
    { name: 'assignee', labelKey: 'NLSChartFieldAssignee', kind: 'dimension', type: 'string' },
    { name: 'board', labelKey: 'NLSChartFieldBoard', kind: 'dimension', type: 'string' },
    { name: 'parentGoal', labelKey: 'NLSChartFieldParentGoal', kind: 'dimension', type: 'string' },
    { name: 'tag', labelKey: 'NLSChartFieldTag', kind: 'dimension', type: 'string' },
    { name: 'overdue', labelKey: 'NLSChartFieldOverdue', kind: 'dimension', type: 'boolean' },
    { name: 'template', labelKey: 'NLSChartFieldTemplate', kind: 'dimension', type: 'string' },
    { name: 'count', labelKey: 'NLSChartFieldCount', kind: 'measure', type: 'number' },
    { name: 'progress', labelKey: 'NLSChartFieldProgress', kind: 'measure', type: 'number' },
    { name: 'doneCount', labelKey: 'NLSChartFieldDoneCount', kind: 'measure', type: 'number' },
    { name: 'overdueCount', labelKey: 'NLSChartFieldOverdueCount', kind: 'measure', type: 'number' },
    { name: 'created', labelKey: 'NLSChartFieldCreated', kind: 'time', type: 'date' },
    { name: 'due', labelKey: 'NLSChartFieldDue', kind: 'time', type: 'date' },
    { name: 'done', labelKey: 'NLSChartFieldDone', kind: 'time', type: 'date' },
  ],
  defaultMeasure: { field: 'count', agg: 'count' },
  defaultTimeField: 'created',
  allowedRoles: ['OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'HELPER'],
  available: true,
}

function toDateSafe(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (val as { toDate: () => Date }).toDate()
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  if (typeof (val as { toMillis?: () => number }).toMillis === 'function') {
    try {
      const ms = (val as { toMillis: () => number }).toMillis()
      const d = new Date(ms)
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  if (typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  if (typeof val === 'string') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

export function buildWorkRow(
  data: Record<string, unknown>,
  id: string,
  isChecklist: boolean = false,
  nowMs: number = Date.now(),
): Record<string, unknown> {
  const dueDate = toDateSafe(data.dueDate)
  const completedAt = toDateSafe(data.completedAt)
  const createdAt = toDateSafe(data.createdAt)

  const isDone = Boolean(completedAt || data.status === 'DONE' || data.isCompleted === true)
  const isOverdue = Boolean(dueDate && !isDone && dueDate.getTime() < nowMs) || Boolean(data.isOverdue)

  const assignee = typeof data.assigneeId === 'string'
    ? data.assigneeId
    : (typeof data.assignee === 'string'
      ? data.assignee
      : (typeof data.assignedTo === 'string' ? data.assignedTo : ''))

  const rowType = isChecklist ? 'CHECKLIST' : (typeof data.type === 'string' ? data.type : '')
  const parentId = isChecklist
    ? (typeof data.parentContainerId === 'string'
      ? data.parentContainerId
      : (typeof data.parentId === 'string' ? data.parentId : ''))
    : (typeof data.parentId === 'string' ? data.parentId : '')

  return {
    id,
    _id: id,
    type: rowType,
    status: typeof data.status === 'string' ? data.status : (isDone ? 'DONE' : 'TODO'),
    priority: typeof data.priority === 'string' ? data.priority : '',
    assignee,
    _assigneeId: assignee,
    board: typeof data.boardId === 'string' ? data.boardId : (typeof data.board === 'string' ? data.board : ''),
    parentGoal: typeof data.parentGoalId === 'string' ? data.parentGoalId : (typeof data.parentGoal === 'string' ? data.parentGoal : ''),
    parentId,
    tag: Array.isArray(data.tags) ? data.tags.join(',') : (typeof data.tag === 'string' ? data.tag : ''),
    overdue: isOverdue,
    template: typeof data.templateSourceId === 'string' ? data.templateSourceId : '',
    count: 1,
    progress: typeof data.progress === 'number' ? data.progress : (isDone ? 100 : 0),
    doneCount: isDone ? 1 : 0,
    overdueCount: isOverdue ? 1 : 0,
    created: createdAt ? createdAt.toISOString() : null,
    due: dueDate ? dueDate.toISOString() : null,
    done: completedAt ? completedAt.toISOString() : null,
    title: typeof data.title === 'string' ? data.title : (typeof data.name === 'string' ? data.name : ''),
  }
}
